import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import PropTypes from 'prop-types';
import React, { useMemo, useCallback } from 'react';
import { contentSelector, labelSelector, newPathSelector, projectsValueSelector, valueSelector } from '../../selectors/index';
import entityComposition from '../../../common/classes/entityComposition';
import { getIssuesArray, getGroupedChecksForProject, getIssuesForCheck, getTvcIssueStableRowKey, selectTvcIssuesBehavior } from './tvcIssuesSectionHelpers';
import TvcIssuesIssueListPanel from './TvcIssuesIssueListPanel';
import TvcIssuesProjectRail from './TvcIssuesProjectRail';
import useTvcIssuesNativeFunctions from './useTvcIssuesNativeFunctions';
import useTvcIssuesProjectLoad from './useTvcIssuesProjectLoad';
import useTvcIssuesUiState from './useTvcIssuesUiState';
const changeActionsShape = PropTypes.shape({
  change: PropTypes.func,
  changeQuietly: PropTypes.func
});

/**
 * TVC Issues Section view.
 *
 * - format.projectsContentPath — content location of the project id → { title, uuid } map.
 * - format.contentPath — where issue payload is read/written in entity content (Redux).
 * - Project pick runs native tvcIssuesBehavior (default source in tvcIssuesSectionHelpers)
 *   via evaluateSource;
 *   optional format.tvcIssuesBehavior (or behaviors[]) overrides when the server needs more
 *   fields than `{ type: 'tvcIssuesBehavior' }`.
 *
 * Left rail: project picker and collapsible checks. List: items for the selected check.
 */
const TvcIssuesSection = _ref => {
  let _ref$value = _ref.value,
    value = _ref$value === void 0 ? null : _ref$value,
    _ref$content = _ref.content,
    content = _ref$content === void 0 ? null : _ref$content,
    _ref$path = _ref.path,
    path = _ref$path === void 0 ? '/' : _ref$path,
    _ref$label = _ref.label,
    label = _ref$label === void 0 ? null : _ref$label,
    _ref$projectsContent = _ref.projectsContent,
    projectsContent = _ref$projectsContent === void 0 ? null : _ref$projectsContent,
    _ref$tvcIssuesBehavio = _ref.tvcIssuesBehavior,
    tvcIssuesBehavior = _ref$tvcIssuesBehavio === void 0 ? null : _ref$tvcIssuesBehavio,
    _ref$parentContentPat = _ref.parentContentPath,
    parentContentPath = _ref$parentContentPat === void 0 ? '/' : _ref$parentContentPat,
    _ref$reduxEntity = _ref.reduxEntity,
    reduxEntity = _ref$reduxEntity === void 0 ? 'form' : _ref$reduxEntity,
    _ref$active = _ref.active,
    active = _ref$active === void 0 ? null : _ref$active,
    _ref$formActions = _ref.formActions,
    formActions = _ref$formActions === void 0 ? null : _ref$formActions,
    _ref$issueActions = _ref.issueActions,
    issueActions = _ref$issueActions === void 0 ? null : _ref$issueActions,
    _ref$reportActions = _ref.reportActions,
    reportActions = _ref$reportActions === void 0 ? null : _ref$reportActions,
    _ref$notificationActi = _ref.notificationActions,
    notificationActions = _ref$notificationActi === void 0 ? null : _ref$notificationActi,
    _ref$pendingOperation = _ref.pendingOperation,
    pendingOperation = _ref$pendingOperation === void 0 ? false : _ref$pendingOperation;
  const changeActions = (() => {
    if (reduxEntity === 'issue') return issueActions;
    if (reduxEntity === 'report') return reportActions;
    return formActions;
  })();
  const change = changeActions === null || changeActions === void 0 ? void 0 : changeActions.change;
  /**
   * Avoid content `change` thunk: pendingOperation + format behaviors retrigger the load effect.
   */
  const changeQuietly = changeActions === null || changeActions === void 0 ? void 0 : changeActions.changeQuietly;
  const _useTvcIssuesUiState = useTvcIssuesUiState({
      projectsContent,
      active,
      change,
      changeQuietly,
      path
    }),
    selectedProjectUuid = _useTvcIssuesUiState.selectedProjectUuid,
    selectedProjectTitle = _useTvcIssuesUiState.selectedProjectTitle,
    expandedAssetTypes = _useTvcIssuesUiState.expandedAssetTypes,
    selectedCheck = _useTvcIssuesUiState.selectedCheck,
    expandedStableRowKey = _useTvcIssuesUiState.expandedStableRowKey,
    removedIssueKeys = _useTvcIssuesUiState.removedIssueKeys,
    animatingOutKeys = _useTvcIssuesUiState.animatingOutKeys,
    handleListRowToggle = _useTvcIssuesUiState.handleListRowToggle,
    handleRestoreSucceeded = _useTvcIssuesUiState.handleRestoreSucceeded,
    handleRowExitAnimationEnd = _useTvcIssuesUiState.handleRowExitAnimationEnd,
    handleProjectChange = _useTvcIssuesUiState.handleProjectChange,
    toggleAssetType = _useTvcIssuesUiState.toggleAssetType,
    selectCheck = _useTvcIssuesUiState.selectCheck,
    isCheckSelected = _useTvcIssuesUiState.isCheckSelected;
  const nativeFunctions = useTvcIssuesNativeFunctions(reduxEntity, notificationActions);
  const _useTvcIssuesProjectL = useTvcIssuesProjectLoad({
      selectedProjectUuid,
      tvcIssuesBehavior,
      active,
      change,
      changeQuietly,
      path,
      parentContentPath,
      reduxEntity,
      nativeFunctions,
      projectsContent
    }),
    loadingIssues = _useTvcIssuesProjectL.loadingIssues,
    refreshIssues = _useTvcIssuesProjectL.refreshIssues;
  const projectEntries = useMemo(() => {
    const m = projectsContent;
    if (m == null || typeof m !== 'object' || Array.isArray(m)) {
      return [];
    }
    return Object.entries(m).map(_ref2 => {
      var _row$title;
      let _ref3 = _slicedToArray(_ref2, 2),
        mapKey = _ref3[0],
        proj = _ref3[1];
      const row = proj && typeof proj === 'object' && !Array.isArray(proj) ? proj : {};
      const title = String((_row$title = row.title) !== null && _row$title !== void 0 ? _row$title : '').trim();
      if (!String(mapKey).trim() || !title) return null;
      return {
        mapKey: String(mapKey),
        title
      };
    }).filter(Boolean).sort((a, b) => a.title.localeCompare(b.title, undefined, {
      sensitivity: 'base'
    }));
  }, [projectsContent]);
  const issuesRaw = useMemo(() => getIssuesArray(value !== null && value !== void 0 ? value : {}), [value]);
  const rowKey = useCallback(item => getTvcIssueStableRowKey(item), []);
  const issues = useMemo(() => issuesRaw.filter(item => !removedIssueKeys.has(rowKey(item)) && !animatingOutKeys.has(rowKey(item))), [issuesRaw, removedIssueKeys, animatingOutKeys, rowKey]);
  const grouped = useMemo(() => getGroupedChecksForProject(issues, selectedProjectTitle || null), [issues, selectedProjectTitle]);
  const listItemsInCheck = useMemo(() => selectedCheck ? getIssuesForCheck(issuesRaw, selectedCheck.projectName, selectedCheck.assetType, selectedCheck.errorType) : [], [issuesRaw, selectedCheck]);
  const listItemsVisible = useMemo(() => listItemsInCheck.filter(item => !removedIssueKeys.has(rowKey(item))), [listItemsInCheck, removedIssueKeys, rowKey]);
  const listHeaderCount = useMemo(() => listItemsVisible.filter(item => !animatingOutKeys.has(rowKey(item))).length, [listItemsVisible, animatingOutKeys, rowKey]);
  const rawValue = value !== null && value !== void 0 ? value : {};
  const hasStoredData = Array.isArray(rawValue) ? rawValue.length > 0 : Object.keys(rawValue).length > 0;
  return /*#__PURE__*/React.createElement("div", {
    className: "tvc-issues-section",
    "data-content-path": path,
    "data-has-data": hasStoredData
  }, /*#__PURE__*/React.createElement("h2", {
    className: "tvc-issues-section__title"
  }, label || 'TVC Issues'), /*#__PURE__*/React.createElement("div", {
    className: "tvc-issues-section__layout"
  }, /*#__PURE__*/React.createElement(TvcIssuesProjectRail, {
    projectEntries: projectEntries,
    selectedProjectUuid: selectedProjectUuid,
    onProjectChange: handleProjectChange,
    onRefreshIssues: refreshIssues,
    loadingIssues: loadingIssues,
    pendingOperation: pendingOperation,
    grouped: grouped,
    expandedAssetTypes: expandedAssetTypes,
    onToggleAssetType: toggleAssetType,
    selectedProjectTitle: selectedProjectTitle,
    isCheckSelected: isCheckSelected,
    onSelectCheck: selectCheck
  }), /*#__PURE__*/React.createElement(TvcIssuesIssueListPanel, {
    selectedCheck: selectedCheck,
    listHeaderCount: listHeaderCount,
    listItemsVisible: listItemsVisible,
    rowKey: rowKey,
    animatingOutKeys: animatingOutKeys,
    expandedStableRowKey: expandedStableRowKey,
    onListRowToggle: handleListRowToggle,
    onRestoreSucceeded: handleRestoreSucceeded,
    onRowExitAnimationEnd: handleRowExitAnimationEnd
  })), (content === null || content === void 0 ? void 0 : content.title) && /*#__PURE__*/React.createElement("span", {
    className: "sr-only"
  }, content.title));
};
TvcIssuesSection.propTypes = {
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  content: PropTypes.shape({
    title: PropTypes.string
  }),
  path: PropTypes.string,
  label: PropTypes.string,
  projectsContent: PropTypes.objectOf(PropTypes.shape({
    title: PropTypes.string,
    uuid: PropTypes.string
  })),
  tvcIssuesBehavior: PropTypes.shape({
    type: PropTypes.string
  }),
  parentContentPath: PropTypes.string,
  reduxEntity: PropTypes.string,
  active: PropTypes.shape({
    id: PropTypes.string
  }),
  formActions: changeActionsShape,
  issueActions: changeActionsShape,
  reportActions: changeActionsShape,
  notificationActions: PropTypes.shape({
    info: PropTypes.func
  }),
  pendingOperation: PropTypes.bool
};
export { TvcIssuesSection as UncomposedTvcIssuesSection };
export default entityComposition(TvcIssuesSection, [], {
  value: valueSelector,
  path: newPathSelector,
  label: labelSelector,
  content: contentSelector,
  projectsContent: projectsValueSelector,
  tvcIssuesBehavior: selectTvcIssuesBehavior,
  active: (state, _ref4) => {
    let reduxEntity = _ref4.reduxEntity;
    return state[reduxEntity].active;
  },
  pendingOperation: (state, _ref5) => {
    var _state$reduxEntity;
    let reduxEntity = _ref5.reduxEntity;
    return !!((_state$reduxEntity = state[reduxEntity]) !== null && _state$reduxEntity !== void 0 && _state$reduxEntity.pendingOperation);
  }
}, ['form', 'issue', 'report', 'notification']);